import React, { useEffect, useState } from "react";
import {saveAs} from 'file-saver';
import t from "../../service/translate.service";
import "./index.css";
import ContractSelector from "../../components/contractSelector";
import { inject, observer } from "mobx-react";
import { api } from "../../service/api.service";
import LoadingPageOverlay from "../../components/LoadingPageOverlay";
import { extractError } from "../../service/response.service";
import { Alert } from "antd";
import NoContent from "../../components/NoContent";
import { ContractsImage } from "../../components/icons";
import pdfSvg from "../../assets/svg/pdf.svg";
import icTmpPreview from "../../assets/ic-tmp-preview.png";
import icTmpDownload from "../../assets/ic-tmp-download.png";
import { Link } from "react-router-dom";
import find from 'lodash/find'
import VoucherRequestButtons from "./VoucherRequestButtons";
import { appFeatureEnabled } from "../../service/appFeatures.service";

const Vouchers = (props) => {
  const { User } = props.contractStore;
  const vouchersPhaseTwoEnabled = appFeatureEnabled('vouchersPhaseTwoEnabled', User?.contracts || []);
  
  const [activeTab, setActiveTab] = useState(vouchersPhaseTwoEnabled ? "request" : "list");
  const [loading, setLoading] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true)
  const [vouchers, setVouchers] = useState([]);
  const [error, setError] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const [contractNumber, setContractNumber] = useState('')
  const getVouchers = async (contractNumber, type) => {
    setLoading(true);
    await api
      .get(`/vouchers/${contractNumber}?type=${type}`)
      .then((res) => {
        setError('')
        setVouchers(res?.data || [])
      })
      .catch((error) => {
        setVouchers([])
        setError(t[extractError(error)] || t["ERROR"])
      });
    setLoading(false);
  };

  const getEligibility = async (contractNum) => {
    setEligibilityLoading(true);
    
    if (!contractNum) {
      setEligibilityLoading(false);
      return;
    }
    
    await api
      .get(`/vouchers/${contractNum}/eligibility`)
      .then((res) => {
        setEligibility(res?.data || null)
      })
      .catch((error) => {
        setEligibility(null)
        console.error('Error fetching eligibility:', error)
      });
    setEligibilityLoading(false);
  };

  const getDocument = async (voucher) => {
    setLoading(true)
    const res = await api.get(`/vouchers/${props?.contractStore?.selectedContract?.contractNumber}/${voucher.Name}/file`, {
      responseType: 'blob',
    }).then((res) => {
      setError('')
      return res
    })
    .catch((error) => {
      setError(t[extractError(error)] || t["ERROR"])
    });
    setLoading(false)
    return res.data
  }

  const downloadDocument = async (voucher) => {
    try {
      setLoading(true)
      setError('')
      const file = await getDocument(voucher);
      saveAs(file, voucher.Name, {});
      await api.post(`/vouchers/${props?.contractStore?.selectedContract?.contractNumber}/${voucher?.UniqueId}/file`,
        {
          Downloaded: true
        })
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setError(t[extractError(error)] || t["ERROR"])
    }
  }

  const setDocumentHidden = async (voucher, hidden) => {
    setLoading(true)
    await api.post(`/vouchers/${props?.contractStore?.selectedContract?.contractNumber}/${voucher?.UniqueId}/file`,
      {
      Hidden: hidden
      })
    .then(async () => {
      if (activeTab && props?.contractStore?.selectedContract?.contractNumber) {
        await getVouchers(
          props?.contractStore?.selectedContract?.contractNumber,
          activeTab
        );
      }
    })
    .catch((error) => {
      setError(t[extractError(error)] || t["ERROR"])
    });
    setLoading(false)
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => {
      if (activeTab && activeTab !== 'request' && contractNumber) {
        const type = activeTab === 'list' ? 'unhidden' : 'hidden';
        await getVouchers(
          contractNumber,
          type
        );
        // Reset eligibility when leaving the Request tab so it refetches on return
        setEligibility(null);
      }
    })();
  }, [activeTab, contractNumber]);

  useEffect(() => {
    (async () => {
      // Fetch eligibility only when on the "Request" tab
      // Re-fetches when switching back to the Request tab or when contract changes
      if (activeTab === 'request') {
        await getEligibility(contractNumber);
      }
    })();
  }, [activeTab, contractNumber]);

  useEffect(() => {
    if (firstLoading) {
      setFirstLoading(false)
      const contractFromParams = urlParams.get('contractNumber')
      if (contractFromParams) {
        const contract = find(props?.contractStore?.activeContracts, { contractNumber: contractFromParams })
        props.contractStore.selectedContract = contract
        setContractNumber(contractFromParams)
      } else {
        setContractNumber(props?.contractStore?.selectedContract?.contractNumber)
      }
    } else {
      setContractNumber(props?.contractStore?.selectedContract?.contractNumber)
    }
  }, [props?.contractStore?.selectedContract?.contractNumber]);

  if (loading) return <LoadingPageOverlay />;

  return (
    <div className="vouchers-page-wrap">
      {error && <Alert message={error} type='error' style={{marginTop: '12px', marginBottom: '12px'}} />}
      <div className="contract-selector-wrap">
        <ContractSelector page='vouchers' label padding contractNumber={contractNumber} />
      </div>
      <div className="tab-wrap">
        {vouchersPhaseTwoEnabled && (
          <button
            id="requestBtn"
            type="button"
            onClick={() => setActiveTab("request")}
            className={`tab-button ${activeTab === "request" ? "active" : ""}`}
          >
            {t["VOUCHERS_REQUEST_TAB"] || "Request a voucher"}
          </button>
        )}
        <button
          id="listBtn"
          type="button"
          onClick={() => setActiveTab("list")}
          className={`tab-button ${activeTab === "list" ? "active" : ""}`}
        >
          {t["VOUCHERS_UNHIDDEN"]}
        </button>
        <button
          id="archivedBtn"
          type="button"
          className={`tab-button ${activeTab === "archived" ? "active" : ""}`}
          onClick={() => setActiveTab("archived")}
        >
          {t["VOUCHERS_HIDDEN"]}
        </button>
      </div>

      {vouchersPhaseTwoEnabled && activeTab === "request" && (
        <VoucherRequestButtons 
          eligibility={eligibility} 
          loading={eligibilityLoading}
          contractNumber={contractNumber}
        />
      )}

      {(activeTab === "list" || activeTab === "archived") && (
      <div className="documents-list" style={{marginBottom: '30px', marginTop: '24px'}}>
        {vouchers?.length > 0 ? (
          vouchers?.map((voucher, i) => {
            return (
              <div key={i} className='item'>
                <div className='container'>
                  <div className='details'>
                    <div className='svg'>
                      <img src={pdfSvg} alt='PDF'/>
                    </div>
                    <div className='group'>
                      <div className='label'>{voucher.Name}</div>
                    </div>
                  </div>
                  <div className='actions'>
                    <Link
                      className='open-preview'
                      to={`/vouchers/${props?.contractStore?.selectedContract?.contractNumber}/${voucher?.Name}/preview`}
                    >
                      <img src={icTmpPreview} alt='Preview'/>
                    </Link>
                    <button
                      className='start-download'
                      onClick={(e) => {
                        downloadDocument(voucher)
                      }}
                    >
                      <img src={icTmpDownload} alt='Download'/>
                    </button>
                    <button
                      className='archive-restore'
                      onClick={(e) => {
                        setDocumentHidden(voucher, !voucher?.ListItemAllFields['Hidden'])
                      }}
                    >
                      {!voucher?.ListItemAllFields['Hidden'] ? t['VOUCHERS_HIDE'] : t['VOUCHERS_UNHIDE']}
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="no-content-wrap">
            <NoContent message={t.NO_VOUCHERS} image={ContractsImage}/>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default inject("contractStore")(observer(Vouchers));
